import { ref, computed, Ref, ComputedRef } from 'vue';
import { defineStore } from 'pinia';
import { OrganizationsResponseBody, GroupsResponseBody, MeResponseBody } from '@ilteducation/my-account-types';
import { getMe } from './api';
import { useConfigHelper } from '@ilteducation/config';

type CachedOrganizationsResponse = OrganizationsResponseBody & { updatedAt: number };
type CachedGroupsResponse = GroupsResponseBody & { updatedAt: number };

export type MyAccountStore = {
  me: Ref<MeResponseBody | undefined>;
  isLoading: ComputedRef<boolean>;
  organizations: Ref<CachedOrganizationsResponse>;
  setOrganization: (orgs: CachedOrganizationsResponse) => void;
  groups: Ref<CachedGroupsResponse>;
  setGroups: (groups: CachedGroupsResponse) => void;
  invalidateGroups: () => void;
  invalidateOrganizations: () => void;
  load: () => Promise<void>;
  reset: () => void;
};

const myAccountStore = defineStore(
  'my-account',
  (): MyAccountStore => {
    const { getLocale } = useConfigHelper();

    const me = ref<MeResponseBody | undefined>(undefined);
    const isLoadingMyAccount = ref(false);

    const organizations = ref<CachedOrganizationsResponse>({
      data: [],
      meta: { totalCount: 0 },
      updatedAt: 0,
    });

    const groups = ref<CachedGroupsResponse>({
      data: [],
      meta: { totalCount: 0 },
      updatedAt: 0,
    });

    const load = async () => {
      if (isLoadingMyAccount.value) {
        console.log('Already loading me');
        return;
      }
      if (!me.value) {
        me.value = {
          uid: '',
          speakingBookRights: false,
          email: '',
          emailVerified: false,
          firstname: 'Unknown',
          lastname: '',
          username: 'Unknown',
          sharedAccount: false,
          hidePasswordChange: false,
          identifiers: [],
        };
      }

      isLoadingMyAccount.value = true;
      try {
        me.value = await getMe();
      } finally {
        isLoadingMyAccount.value = false;
      }
    };

    const reset = () => {
      me.value = undefined;
      isLoadingMyAccount.value = false;
      organizations.value = {
        data: [],
        meta: { totalCount: 0 },
        updatedAt: 0,
      };
      groups.value = {
        data: [],
        meta: { totalCount: 0 },
        updatedAt: 0,
      };
    };

    const setOrganization = (orgs: CachedOrganizationsResponse) => {
      organizations.value = {
        ...orgs,
        data: orgs.data.sort((a, b) => a.name.localeCompare(b.name, getLocale())),
      };
    };

    const setGroups = (newGroups: CachedGroupsResponse) => {
      groups.value = {
        ...newGroups,
        data: newGroups.data.sort((a, b) => a.name.localeCompare(b.name, getLocale())),
      };
    };

    const invalidateGroups = () => {
      groups.value = { ...groups.value, updatedAt: 0 };
    };

    const invalidateOrganizations = () => {
      organizations.value = { ...organizations.value, updatedAt: 0 };
    };

    return {
      me,
      isLoading: computed(() => isLoadingMyAccount.value),
      organizations,
      setOrganization,
      groups,
      setGroups,
      invalidateGroups,
      invalidateOrganizations,
      load,
      reset,
    };
  },
  {
    // @ts-ignore
    persist: true,
  },
);

export default myAccountStore;
